/**
 * PPS — John Person's Pivot Point Moving Average System, ported from the
 * "JS - Buy Sell HT" EasyLanguage indicator (JAM Strategy Trading, 2016). The
 * rules, per bar:
 *
 *   pivot = TypicalPrice = (H + L + C) / 3
 *   MA1   = MA(pivot, ma1Len)   // default 1 → a 1-period MA IS the pivot
 *   MA3   = MA(pivot, ma2Len)   // default 3 → the 3-period pivot MA
 *   MA = SMA (default) or XAverage (EMA, the EL source's default) — `maType`.
 *
 * DEFAULT = SMA since 2026-07-13: the SMA variant IS the SweeterTrade screener's
 * CprUp / CprDown (aka the "JAS CPR" TradingView study) — verified bar-exact
 * (300/300 bars, all 63 flips) against a JAS CPR daily export of XOM, and 79/80
 * on a cross-universe sample of the screener's flags (the residual is
 * adjusted-vs-raw bar drift on high-dividend names, not rule differences).
 * CprUp = 1 in that screener == this flip == +1 on the LATEST bar. Note
 * close > MA1 is close > (H+L)/2 (an upper-half close), so a Buy bar is always
 * a green, close-up, upper-half bar — the first since the last Sell.
 *
 *   Buy  (ALL true): MA1 > MA3, Close > Open, Close > MA1, Close > MA3, + latch
 *   Sell (ALL true): MA1 < MA3, Close < Open, Close < MA1, Close < MA3, + latch
 *
 * The latch (BuyCondition5 / SellCondition5) arms on the OPPOSITE alignment and
 * is consumed the moment a signal fires, so each side fires ONCE per regime and
 * signals strictly alternate. computePps returns the rising edge of
 * ReadyToBuy / ReadyToSell: +1 on a Buy flip, -1 on a Sell flip, 0 otherwise.
 */
import { emaArr } from "./jpHistogram";
import { familyGroup } from "../indicatorGroups";
import { resampleOHLC, htIndexPerDay, type HtFreq } from "./weekly";
import { TF_DWM, TF_PARAM } from "./timeframe";
import type { OHLCV, IndicatorParams, SharedParam, SharedIndicatorDef } from "../types";

/** Pivot + MA1 + MA3 rails — the lines the book system is judged against. */
export function computePpsRails(
  highs: ReadonlyArray<number>,
  lows: ReadonlyArray<number>,
  closes: ReadonlyArray<number>,
  ma1Len = 1,
  ma2Len = 3,
  maType: "ema" | "sma" = "sma",
): { pivot: number[]; ma1: number[]; ma3: number[] } {
  const n = closes.length;
  const pivot = new Array<number>(n);
  for (let i = 0; i < n; i++) pivot[i] = (highs[i]! + lows[i]! + closes[i]!) / 3;
  const smaExact = (x: number[], L: number): number[] => {
    if (L <= 1) return x;
    const out = new Array<number>(n).fill(NaN);
    for (let i = L - 1; i < n; i++) {
      let s = 0;
      for (let k = i - L + 1; k <= i; k++) s += x[k]!;
      out[i] = s / L;
    }
    return out;
  };
  const ma = maType === "sma" ? smaExact : emaArr;
  return {
    pivot,
    ma1: ma(pivot, Math.max(1, Math.round(ma1Len))),
    ma3: ma(pivot, Math.max(1, Math.round(ma2Len))),
  };
}

export function computePps(
  opens: ReadonlyArray<number>,
  highs: ReadonlyArray<number>,
  lows: ReadonlyArray<number>,
  closes: ReadonlyArray<number>,
  ma1Len = 1,
  ma2Len = 3,
  maType: "ema" | "sma" = "sma",
): number[] {
  const n = closes.length;
  const flip = new Array<number>(n).fill(0);
  if (n === 0) return flip;
  // SMA must be an EXACT windowed mean here (not jpHistogram's running-sum
  // smaArr): the Buy/Sell conditions are strict > / < against the pivot, and
  // real ties happen (e.g. close == pivot to the penny). The running sum
  // accumulates ~1e-14 float drift over a 760-bar window, which broke such a
  // tie the wrong way (BMY 2026-07-07) and desynced the latch from the
  // SweeterTrade screener. A length-1 SMA is the series itself.
  const { ma1, ma3: ma2 } = computePpsRails(highs, lows, closes, ma1Len, ma2Len, maType);

  let ready = 0;        // +1 = ReadyToBuy, -1 = ReadyToSell (latched, persists)
  let buy5 = false;     // BuyCondition5 — armed by a bearish alignment, consumed on a buy
  let sell5 = false;    // SellCondition5 — armed by a bullish alignment, consumed on a sell
  for (let i = 0; i < n; i++) {
    if (!Number.isFinite(ma1[i]!) || !Number.isFinite(ma2[i]!)) continue;
    const c = closes[i]!, o = opens[i]!, m1 = ma1[i]!, m3 = ma2[i]!;
    const b1 = m1 > m3, b2 = c > o, b3 = c > m1, b4 = c > m3;
    const s1 = m1 < m3, s2 = c < o, s3 = c < m1, s4 = c < m3;
    if (s1) buy5 = true;   // arm a buy while bearishly aligned
    if (b1) sell5 = true;  // arm a sell while bullishly aligned
    if (b1 && b2 && b3 && b4 && buy5) { if (ready !== 1) flip[i] = 1; ready = 1; }
    if (s1 && s2 && s3 && s4 && sell5) { if (ready !== -1) flip[i] = -1; ready = -1; }
    if (b1 && b2 && b3 && b4) buy5 = false;   // consume the arm once the setup is complete
    if (s1 && s2 && s3 && s4) sell5 = false;
  }
  return flip;
}

const numOr = (v: number | undefined, d: number) =>
  typeof v === "number" && Number.isFinite(v) ? v : d;

const PPS_PARAMS: ReadonlyArray<SharedParam> = [
  { id: "ma1Len", label: "Pivot MA1", kind: "number", default: 1, min: 1, max: 50, step: 1 },
  { id: "ma2Len", label: "Pivot MA2", kind: "number", default: 3, min: 1, max: 50, step: 1 },
  // SMA default = the SweeterTrade CPR / "JAS CPR" variant (see header note).
  { id: "maType", label: "MA type", kind: "select", default: 1, choices: [{ value: 0, label: "EMA" }, { value: 1, label: "SMA" }] },
];


// ── Person Signal / Trend (PPS-based, timeframe-select) ──────────────────
// "Signal" = the PPS buy/sell flip (Up +1 / Down -1 / No signal 0) on the chosen
// timeframe — the outcome of the PPS Signal above. "Trend" = the LAST non-zero
// signal latched forward (Up / Down). Daily runs on daily bars; Weekly / Monthly
// resample (in-progress bucket included) and project onto daily bars by period.
// Absent maType → SMA (the default); an explicit 0 / "0" keeps EMA.
const ppsTfArgs = (p: IndicatorParams) =>
  [
    numOr(p.ma1Len, 1),
    numOr(p.ma2Len, 3),
    (p.maType != null && Number(p.maType) === 0 ? "ema" : "sma") as "sma" | "ema",
  ] as const;

const tfFreq = (tf: number | undefined): HtFreq | "day" =>
  tf === 2 ? "month" : tf === 1 ? "week" : "day";

/** Latch the last non-zero flip forward (0 until the first signal). Exported so
 *  the worker bakes jp_trend_* from the SAME latch the indicator uses. */
export const latchTrend = (flip: ReadonlyArray<number>): number[] => {
  const out = new Array<number>(flip.length).fill(0);
  let last = 0;
  for (let i = 0; i < flip.length; i++) {
    if (flip[i] !== 0) last = flip[i]!;
    out[i] = last;
  }
  return out;
};

/** PPS flip (`trend=false`) or its latched trend (`trend=true`) on a timeframe,
 *  projected onto daily bars (each day → its period's value). */
function ppsTfSeries(
  data: OHLCV, p: IndicatorParams, freq: HtFreq | "day", trend: boolean,
): (number | null)[] {
  const { opens, highs, lows, closes, times } = data;
  const n = closes.length;
  if (!opens || !highs || !lows) return new Array(n).fill(null);
  const [a, b, t] = ppsTfArgs(p);
  if (freq === "day") {
    const flip = computePps(opens, highs, lows, closes, a, b, t);
    return trend ? latchTrend(flip) : flip;
  }
  if (!times || times.length !== n) return new Array(n).fill(null);
  const h = resampleOHLC(times, opens, highs, lows, closes, freq);
  if (h.c.length < 3) return new Array(n).fill(null);
  const flip = computePps(h.o, h.h, h.l, h.c, a, b, t);
  const series = trend ? latchTrend(flip) : flip;
  const idx = htIndexPerDay(times, h.t);
  return times.map((_, i) => { const k = idx[i]!; return k >= 0 && k < series.length ? series[k]! : null; });
}

const UP_DOWN_NO = { pos: "Up", neg: "Down", zero: "—" } as const;

/** JP Signal — the PPS buy/sell flip on the chosen timeframe (Up / Down / —).
 *  panelKey = the Daily served field (jp_signal_d → signal_d); Weekly / Monthly
 *  resolve instantly via TF_PRECOMPUTED (signal_w / signal_m). */
export const JP_SIGNAL: SharedIndicatorDef = {
  id: "mpp-jp-signal",
  label: "Afiat - Pivot Close Reversal",
  group: familyGroup("pps"), // → "Pivot Studies"
  kind: "state",
  format: "state",
  stateLabels: UP_DOWN_NO,
  panelKey: "signal_d",
  params: [TF_DWM, ...PPS_PARAMS],
  computeSeries: (d, p) => ppsTfSeries(d, p, tfFreq(p[TF_PARAM]), false),
};

/** JP Trend — the last PPS signal latched forward (Up / Down). panelKey = the
 *  Daily served field (jp_trend_d → cpr_d, a historical alias); Weekly / Monthly
 *  via TF_PRECOMPUTED (cpr_w / trend_m). */
export const JP_TREND: SharedIndicatorDef = {
  id: "mpp-jp-trend",
  label: "Afiat - Pivot Close Reversal",
  group: familyGroup("pps"), // → "Pivot Studies"
  kind: "state",
  format: "state",
  stateLabels: UP_DOWN_NO,
  panelKey: "cpr_d",
  params: [TF_DWM, ...PPS_PARAMS],
  computeSeries: (d, p) => ppsTfSeries(d, p, tfFreq(p[TF_PARAM]), true),
};

export const PPS_INDICATORS: ReadonlyArray<SharedIndicatorDef> = [JP_SIGNAL, JP_TREND];
